"use client";

import FilosofiLogoCard from "@/app/Components/FilosofiLogoCard";
import { FilosofiLogos } from "../constant";
import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";

const FilosofiLogo = () => {
  const cardRef = useRef(null);
  const [cardWidth, setCardWidth] = useState(0);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [isManualScroll, setIsManualScroll] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);

  const sectionRef = useRef(null);
  const logosContainerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 0", "end 1"],
  });

  useEffect(() => {
    const updateWidth = () => {
      if (logosContainerRef.current) {
        setContainerWidth(logosContainerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  useEffect(() => {
    if (cardRef.current) {
      setCardWidth(cardRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (cardRef.current) {
        setCardWidth(cardRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const cardGap = 40;
  const cardWidthTotal = cardWidth + cardGap;

  const translateX = useTransform(
    scrollYProgress,
    [0, 0.33, 0.66, 1],
    [
      0,
      -(cardWidthTotal * 1 - 10),
      -(cardWidthTotal * 2 - 20),
      -cardWidthTotal * 3,
    ]
    // [(cardWidth/3), -(((cardWidth/1.5) * 1) - 20), -(((cardWidth/0.5) * 2) - 10), -(cardWidth/1) * 3]
  );

  const smoothMove = useSpring(translateX, { stiffness: 180, damping: 30 });

  const buttons = FilosofiLogos.map((filosofi) => ({
    no: filosofi.no,
    text: filosofi.title,
  }));

  const handleButtonClick = (index) => {
    if (activeCardIndex === index) return;
    setIsManualScroll(true);
    setActiveCardIndex(index);

    const targetElement = document.getElementById(index + 1);
    if (targetElement) {
      setActiveCardIndex(undefined);
      targetElement.scrollIntoView({ behavior: "smooth", block: "start" });

      setTimeout(() => {
        setActiveCardIndex(index);
        setIsManualScroll(false);
      }, 1000);
    }
  };

  useEffect(() => {
    if (isManualScroll) return;
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const isScrollingDown = currentScrollY > lastScrollY;
      lastScrollY = currentScrollY;

      for (let i = 0; i <= 5; i++) {
        const section = document.getElementById(i);
        if (section) {
          const rect = section.getBoundingClientRect();
          if (isScrollingDown) {
            if (rect.top >= 0) {
              setActiveCardIndex(i > 2 ? i - 2 : 0);
              break;
            }
          } else {
            if (rect.bottom >= 0) {
              setActiveCardIndex(i < 3 ? i : 3);
              break;
            }
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isManualScroll]);

  return (
    <div ref={sectionRef} className="relative h-[400vh]">
      <div className="absolute flex flex-col w-full h-full">
        <div id={0} className="h-[0.1px] w-full" />
        <div id={1} className="h-screen w-full" />
        <div id={2} className="h-screen w-full" />
        <div id={3} className="h-screen w-full" />
        <div id={4} className="h-screen w-full" />
        <div id={5} className="h-[0.1px] w-full" />
        {/* <div id={0} className="h-[0.1px] w-full bg-purple-500" />
        <div id={1} className="h-screen w-full bg-red-500" />
        <div id={2} className="h-screen w-full bg-green-500" />
        <div id={3} className="h-screen w-full bg-blue-500" />
        <div id={4} className="h-screen w-full bg-yellow-500" />
        <div id={5} className="h-[0.1px] w-full bg-purple-500" /> */}
      </div>
      <div className="sticky top-0 flex flex-col justify-center items-center px-5 sm:px-10 md:px-20 pt-20 pb-[15px] -mt-20 text-white w-full">
        <div className="flex flex-col w-full h-[calc(100vh-95px)] gap-5 md:gap-8 p-3 sm:p-5 md:p-10 rounded-2xl md:rounded-[50px] bg-[url('/image/bg-filosofi-logo.webp')] bg-cover bg-center bg-no-repeat overflow-hidden drop-shadow-md hover:drop-shadow-xl">
          <div className="w-full text-left font-bold text-xl sm:text-2xl md:text-3xl lg:text-4xl">
            Makna Filosofis di Balik Desain Logo yang Inspiratif
          </div>
          <div className="flex flex-row w-full lg:w-full gap-2 md:gap-5 lg:gap-10 overflow-x-scroll no-scrollbar whitespace-nowrap">
            {buttons.map((button, index) => (
              <button
                key={index}
                aria-label={`go to logo ${button.text}`}
                className={`flex-shrink-0 lg:flex-shrink-1 flex justify-center items-center 
                  w-40 md:w-52 lg:w-full cursor-pointer border 
                  ${
                    activeCardIndex === index
                      ? "bg-cust-orange border-cust-orange text-cust-black"
                      : "bg-transparent border-white text-white"
                  } 
                  px-5 py-3 md:py-4 rounded-full font-medium 
                  hover:bg-cust-orange hover:text-cust-black hover:border-cust-orange 
                  transition-all duration-300 ease-in-out`}
                onClick={() => handleButtonClick(index)}
              >
                {button.text}
              </button>
            ))}
          </div>

          <div className="relative w-full h-full">
            <motion.div
              ref={logosContainerRef}
              className="flex flex-row justify-start items-stretch gap-10 h-full"
              style={{
                x: smoothMove,
                width: FilosofiLogos.length * cardWidthTotal,
              }}
            >
              {FilosofiLogos.map((filosofi, index) => (
                <div key={index} style={{ width: `${cardWidth}px` }}>
                  <FilosofiLogoCard
                    image={filosofi.image}
                    title={filosofi.title}
                    description={filosofi.description}
                    cardWidth={cardWidth}
                    ref={cardRef}
                  />
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilosofiLogo;
